import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { type ReportingBasis } from './types'

interface ContextValue {
  basis?: ReportingBasis
  setBasis?: (next: ReportingBasis) => Promise<void>
}

const ReportingBasisContext = createContext<ContextValue>({})

export const useReportingBasis = () => useContext(ReportingBasisContext)

interface ReportingBasisProviderProps {
  enabled: boolean
  businessId?: string
  bearerToken?: string | null
  layerApiUrl: string
  onMutate: () => void
  children: ReactNode
}

export const ReportingBasisProvider = ({
  enabled,
  businessId,
  bearerToken,
  layerApiUrl,
  onMutate,
  children,
}: ReportingBasisProviderProps) => {
  const [basis, setBasisState] = useState<ReportingBasis | undefined>(undefined)
  const active = enabled && !!businessId && !!bearerToken

  useEffect(() => {
    if (!active) return
    let cancelled = false
    void (async () => {
      try {
        const response = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/accounting-config`,
          {
            headers: {
              Authorization: `Bearer ${bearerToken}`,
              'Content-Type': 'application/json',
            },
          },
        )
        if (!response.ok || cancelled) return
        const json = await response.json()
        const raw = (json?.data ?? json)?.default_reporting_basis as
          | string
          | null
          | undefined
        if (cancelled) return
        setBasisState(raw === 'CASH' || raw === 'ACCRUAL' ? raw : 'ACCRUAL')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Reporting basis fetch error:', error)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [active, businessId, bearerToken, layerApiUrl])

  const setBasis = useCallback(
    async (next: ReportingBasis) => {
      if (!active) return
      const previous = basis
      setBasisState(next)
      try {
        const response = await fetch(
          `${layerApiUrl}/v1/businesses/${businessId}/accounting-config`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${bearerToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ default_reporting_basis: next }),
          },
        )
        if (!response.ok) {
          throw new Error(
            `Failed to update reporting basis (HTTP ${response.status})`,
          )
        }
        onMutate()
      } catch (error) {
        setBasisState(previous)
        // eslint-disable-next-line no-console
        console.error('Reporting basis update failed:', error)
        throw error
      }
    },
    [active, basis, bearerToken, businessId, layerApiUrl, onMutate],
  )

  const value: ContextValue = active ? { basis, setBasis } : {}

  return (
    <ReportingBasisContext.Provider value={value}>
      {children}
    </ReportingBasisContext.Provider>
  )
}
